// Navigation model for the unified dashboard, shared by the desktop top nav
// (UnifiedHeader) and the mobile grouped sidebar (DashboardMobileNav).

import { useLocation, useNavigate } from 'react-router-dom';
import type { IconType } from 'react-icons';
import {
  FiBarChart2,
  FiUsers,
  FiTrello,
  FiFileText,
  FiCalendar,
  FiDatabase,
  FiActivity,
  FiClipboard,
  FiGrid,
  FiPieChart,
  FiSend,
} from 'react-icons/fi';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';
import { FeaturePermissions } from '@/lib/sdk/auth/types';

export interface DashboardNavLink {
  key: string;
  label: string;
  path: string;
  icon: IconType;
}

/** Sidebar group a section is listed under on mobile. */
export type DashboardNavGroup = 'Workflows' | 'Organisation' | 'Insights';

/** A top-level nav entry. Sections with more than one link get sub-tabs on desktop. */
export interface DashboardNavSection {
  key: string;
  label: string;
  icon: IconType;
  group: DashboardNavGroup;
  links: DashboardNavLink[];
}

export interface DashboardNav {
  orgName: string;
  userName: string;
  sections: DashboardNavSection[];
  /** Role-gated pages (super admin / org admin); empty for everyone else. */
  adminLinks: DashboardNavLink[];
  /** Key of the link matching the current URL, or null. */
  activeLinkKey: string | null;
  /** Key of the section containing the active link, or null. */
  activeSectionKey: string | null;
  switchOrg: () => void;
  logout: () => Promise<void>;
}

export const DASHBOARD_NAV_GROUPS: DashboardNavGroup[] = ['Workflows', 'Organisation', 'Insights'];

export function useDashboardNav(): DashboardNav | null {
  const { user, currentOrg, logout, userPermission, featurePermissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!currentOrg) return null;

  const orgId = currentOrg.id;
  const isSuperAdmin = user?.is_super_admin ?? false;
  const base = `/dashboard/${orgId}`;

  const hasFeature = (featureKey: keyof FeaturePermissions): boolean =>
    isSuperAdmin || helpers.hasFeature(featurePermissions, userPermission, isSuperAdmin, featureKey);

  // Workflows is split into Templates + Tasks (#681/#721 Phase 7b/7d). The
  // `workflows/ai`, `create-workflow`, `workflows/active` and
  // `workflows/instant` routes stay unlinked; the last two redirect to Tasks.
  const allSections: (DashboardNavSection & { featureKey: keyof FeaturePermissions })[] = [
    {
      key: 'workflows',
      label: 'Workflows',
      icon: FiTrello,
      group: 'Workflows',
      featureKey: 'workflows',
      links: [
        { key: 'templates', label: 'Templates', path: `${base}/workflows`, icon: FiFileText },
        { key: 'tasks', label: 'Tasks', path: `${base}/workflows/tasks`, icon: FiActivity },
      ],
    },
    {
      key: 'master-data',
      label: 'Master data',
      icon: FiDatabase,
      group: 'Organisation',
      featureKey: 'master_data',
      links: [{ key: 'master-data', label: 'Master data', path: `${base}/master-data`, icon: FiDatabase }],
    },
    {
      key: 'team',
      label: 'Team',
      icon: FiUsers,
      group: 'Organisation',
      featureKey: 'team',
      links: [{ key: 'team', label: 'Team', path: `${base}/team`, icon: FiUsers }],
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: FiBarChart2,
      group: 'Insights',
      featureKey: 'reports',
      links: [{ key: 'reports', label: 'Reports', path: `${base}/reports`, icon: FiBarChart2 }],
    },
    {
      key: 'reminders',
      label: 'Reminders',
      icon: FiCalendar,
      group: 'Insights',
      featureKey: 'nudges',
      links: [{ key: 'reminders', label: 'Reminders', path: `${base}/nudges`, icon: FiCalendar }],
    },
  ];

  const sections: DashboardNavSection[] = allSections
    .filter((section) => hasFeature(section.featureKey))
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .map(({ featureKey, ...section }) => section);

  const isOrgAdmin = isSuperAdmin || userPermission === 'admin';
  const adminLinks: DashboardNavLink[] = [
    ...(isOrgAdmin
      ? [{ key: 'delivery-logs', label: 'Delivery logs', path: `${base}/delivery-logs`, icon: FiClipboard }]
      : []),
    ...(isSuperAdmin
      ? [
          { key: 'profiling', label: 'Profiling', path: `${base}/profiling`, icon: FiActivity },
          { key: 'bulk-qr-codes', label: 'Bulk QR codes', path: `${base}/bulk-qr-codes`, icon: FiGrid },
          { key: 'metabase-reports', label: 'Metabase reports', path: `${base}/metabase-reports`, icon: FiPieChart },
          { key: 'instant-flow', label: 'Instant task Flow', path: `${base}/instant-flow`, icon: FiSend },
        ]
      : []),
  ];

  // The longest matching path wins, so Tasks (/workflows/tasks) doesn't also
  // light up Templates (/workflows), while workflow detail pages under
  // /workflows/... still resolve to Templates.
  const allLinks = [...sections.flatMap((section) => section.links), ...adminLinks];
  const pathname = location.pathname;
  const activeLink = allLinks
    .filter((link) => pathname === link.path || pathname.startsWith(link.path + '/'))
    .sort((left, right) => right.path.length - left.path.length)[0];
  const activeLinkKey = activeLink?.key ?? null;
  const activeSectionKey =
    sections.find((section) => section.links.some((link) => link.key === activeLinkKey))?.key ?? null;

  return {
    orgName: currentOrg.name,
    userName: user?.name ?? '',
    sections,
    adminLinks,
    activeLinkKey,
    activeSectionKey,
    switchOrg: () => navigate('/client/orgs'),
    logout: async () => {
      await logout();
      navigate('/client/login');
    },
  };
}
